import React, { useState, useEffect, useMemo } from "react"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  Spinner,
  FormGroup,
  Badge,
  Form,
} from "reactstrap"
import TableContainer from "components/Common/TableContainer"
import axios from "axios"
import apiURL from "url" // same as your project import
import toastr from "toastr"
import "toastr/build/toastr.min.css"

const NewContactLeads = () => {
  document.title = "CRM Leads"

  const [addLeadModal, setAddLeadModal] = useState(false)
  const [form, setForm] = useState({
    name: "",
    contact: "",
    email: "",
    city: "",
    service_need: "",
    notes: "",
  })

  const [leads, setLeads] = useState([])
  const [selectedLead, setSelectedLead] = useState(null)
  const [modal, setModal] = useState(false)
  const [loading, setLoading] = useState(false)

  const role = (localStorage.getItem("role") || "").toLowerCase()

  const canChangeStatus = ["super_admin", "admin", "manager"].includes(role)
  const canDelete = ["super_admin", "admin"].includes(role)

  const getStatusColor = status => {
    switch (status.toLowerCase()) {
      case "new":
        return "primary"
      case "pending":
        return "warning"
      case "activated":
        return "success"
      case "cancel":
        return "danger"
      default:
        return "secondary"
    }
  }

  const toggleModal = (lead = null) => {
    if (lead) setSelectedLead(lead)
    setModal(!modal)
  }

  const closeModal = () => {
    setModal(false)
  }

  const toggleAddModal = () => {
    setAddLeadModal(!addLeadModal)
  }

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const fetchNewLeads = async () => {
    try {
      setLoading(true)
      const { data } = await axios.get(`${apiURL}/leads?status=new`)
      if (data.status === "success") setLeads(data.data)
    } catch (err) {
      console.error("Failed to fetch new leads:", err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchNewLeads()
  }, [])

  const handleDelete = async id => {
    if (!window.confirm("Are you sure you want to delete this lead?")) return
    try {
      await axios.delete(`${apiURL}/leads/${id}`, {
        headers: { "x-user-role": role },
      })
      fetchNewLeads()
      toastr.success("Lead deleted successfully")
    } catch (err) {
      toastr.error("Failed to delete lead")
      console.error(err)
    }
  }

  const handleStatusChange = async (id, status) => {
    try {
      await axios.post(
        `${apiURL}/leads/${id}/status`,
        { status },
        {
          headers: { "x-user-role": role },
        }
      )
      fetchNewLeads()
      toastr.success("Status updated successfully")
    } catch (err) {
      toastr.error("Failed to update status")
      console.error("Failed to update status:", err)
    }
  }

  const handleSubmit = async e => {
    e.preventDefault()
    try {
      const { data } = await axios.post(
        `${apiURL}/leads`,
        {
          ...form,
          status: "pending", // default
        },
        {
          headers: { "x-user-role": localStorage.getItem("role") },
        }
      )

      if (data.status === "success") {
        fetchNewLeads()
        toastr.success("Lead created successfully")
        setForm({
          name: "",
          contact: "",
          email: "",
          city: "",
          service_need: "",
          notes: "",
        })
        toggleAddModal()
      } else {
        toastr.error(data.message)
      }
    } catch (err) {
      toastr.error("Error creating lead")
      console.error("Error creating lead:", err)
    }
  }

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        className: "fw-semibold",
      },
      {
        Header: "Contact",
        accessor: "contact",
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "City",
        accessor: "city",
      },
      {
        Header: "Service Need",
        accessor: "service_need",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <Badge color={getStatusColor(value)} className="text-uppercase">
            {value}
          </Badge>
        ),
      },
      {
        Header: "Actions",
        Cell: ({ row }) => {
          const lead = row.original
          return (
            <div className="d-flex gap-2">
              <Button
                size="sm"
                color="info"
                outline
                onClick={() => toggleModal(lead)}
                className="btn-sm px-3"
              >
                View
              </Button>

              {canDelete && (
                <Button
                  size="sm"
                  color="danger"
                  outline
                  onClick={() => handleDelete(lead.id)}
                  className="btn-sm px-3"
                >
                  Delete
                </Button>
              )}
            </div>
          )
        },
      },
    ],
    [canChangeStatus, canDelete]
  )

  return (
    <div className="page-content">
      <Container fluid>
        <Row className="mb-4">
          <Col xs="12">
            <div className="d-flex justify-content-between align-items-center">
              <h4 className="mb-0 fw-semibold">Contact Leads</h4>
              {(canChangeStatus || canDelete) && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={toggleAddModal}
                  className="btn-sm px-4"
                >
                  + New Lead
                </Button>
              )}
            </div>
          </Col>
        </Row>

        <Card className="shadow-sm border-0">
          <CardBody className="p-4">
            {loading ? (
              <div className="text-center p-5">
                <Spinner color="primary" className="mb-3" />
                <p className="text-muted">Loading leads...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <TableContainer
                  columns={columns}
                  data={leads}
                  isGlobalFilter={true}
                  customPageSize={10}
                  className="custom-table table-centered mb-0"
                  divClass="table-responsive"
                  tableProps={{
                    className: "table table-hover align-middle",
                  }}
                />
              </div>
            )}
          </CardBody>
        </Card>

        {/* View/Edit Modal */}
        <Modal
          isOpen={modal}
          toggle={closeModal}
          centered
          size="md"
          className="modal-dialog-shadow"
        >
          <ModalHeader toggle={closeModal} className="bg-light border-bottom">
            Lead Details
          </ModalHeader>
          <ModalBody className="p-4">
            {selectedLead && (
              <dl className="row mb-0">
                <dt className="col-sm-4 fw-semibold text-muted mb-1">Name</dt>
                <dd className="col-sm-8 mb-3">{selectedLead.name}</dd>

                <dt className="col-sm-4 fw-semibold text-muted mb-1">
                  Contact
                </dt>
                <dd className="col-sm-8 mb-3">{selectedLead.contact}</dd>

                <dt className="col-sm-4 fw-semibold text-muted mb-1">Email</dt>
                <dd className="col-sm-8 mb-3">{selectedLead.email}</dd>

                <dt className="col-sm-4 fw-semibold text-muted mb-1">City</dt>
                <dd className="col-sm-8 mb-3">{selectedLead.city}</dd>

                <dt className="col-sm-4 fw-semibold text-muted mb-1">
                  Service Need
                </dt>
                <dd className="col-sm-8 mb-3">{selectedLead.service_need}</dd>

                <dt className="col-sm-4 fw-semibold text-muted mb-1">Notes</dt>
                <dd className="col-sm-8 mb-3">{selectedLead.notes}</dd>

                <dt className="col-sm-4 fw-semibold text-muted mb-2">Status</dt>
                <dd className="col-sm-8">
                  {canChangeStatus ? (
                    <select
                      className="form-select form-select-sm"
                      value={selectedLead.status}
                      onChange={async e => {
                        await handleStatusChange(
                          selectedLead.id,
                          e.target.value
                        )
                        closeModal()
                      }}
                    >
                      <option value="new">New</option>
                      <option value="pending">Pending</option>
                      <option value="activated">Activated</option>
                      <option value="cancel">Cancel</option>
                    </select>
                  ) : (
                    <Badge
                      color={getStatusColor(selectedLead.status)}
                      className="text-uppercase fs-6"
                    >
                      {selectedLead.status}
                    </Badge>
                  )}
                </dd>
              </dl>
            )}
          </ModalBody>
          <ModalFooter className="border-top bg-light">
            <Button color="secondary" onClick={closeModal} className="px-4">
              Close
            </Button>
          </ModalFooter>
        </Modal>

        {/* Add Lead Modal */}
        <Modal
          isOpen={addLeadModal}
          toggle={toggleAddModal}
          centered
          size="lg"
          className="modal-dialog-shadow"
        >
          <ModalHeader
            toggle={toggleAddModal}
            className="bg-light border-bottom"
          >
            Create New Lead
          </ModalHeader>
          <Form onSubmit={handleSubmit}>
            <ModalBody className="p-4">
              <Row>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label className="form-label fw-semibold">
                      Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      className="form-control"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label className="form-label fw-semibold">
                      Contact <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="contact"
                      value={form.contact}
                      onChange={handleChange}
                      className="form-control"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label className="form-label fw-semibold">
                      Email <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      className="form-control"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup className="mb-3">
                    <Label className="form-label fw-semibold">
                      City <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="city"
                      value={form.city}
                      onChange={handleChange}
                      className="form-control"
                      required
                    />
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup className="mb-3">
                    <Label className="form-label fw-semibold">
                      Service Need <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="service_need"
                      value={form.service_need}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      <option value="">Select Service Need</option>
                      <option value="COD">COD</option>
                      <option value="International">International</option>
                      <option value="Courier">Courier</option>
                      <option value="Warehouse">Warehouse</option>
                      <option value="MOFA">MOFA</option>
                      <option value="Apostille">Apostille</option>
                      <option value="Franchise">Franchise</option>
                    </Input>
                  </FormGroup>
                </Col>
                <Col md={12}>
                  <FormGroup className="mb-3">
                    <Label className="form-label fw-semibold">Notes</Label>
                    <Input
                      type="textarea"
                      name="notes"
                      value={form.notes}
                      onChange={handleChange}
                      className="form-control"
                      rows="3"
                      placeholder="Enter any additional notes..."
                    />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter className="border-top bg-light">
              <Button type="submit" color="primary" className="px-4 me-2">
                Create Lead
              </Button>
              <Button
                type="button"
                color="secondary"
                onClick={toggleAddModal}
                className="px-4"
              >
                Cancel
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </Container>
    </div>
  )
}

export default NewContactLeads
